import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { twMerge } from 'tailwind-merge';

import { Button } from '@boss/ui';

import type { OrderImportStep } from '../types';
import OrderImportFileUpload from './OrderImportFileUpload';

type OrderImportInitialViewProps = {
  description: string;
  steps: Array<{ id: OrderImportStep; number: number; title: string; description: string }>;
  downloadButtonLabel: string;
  uploadTitle: string;
  uploadSupportedText: string;
  processingError: string;
  isTemplateLinkLoading: boolean;
  isDownloadDisabled: boolean;
  downloadIcon: IconDefinition;
  colorDisclaimer?: string;
  onTemplateDownloadClick: () => void;
  onFileSelected: (file: File) => void;
  onDismissError?: () => void;
};

const SectionDescriptionStyles = cva('mb-8 max-w-[900px] text-lg text-orderImport-step-description-text');
const StepsGridStyles = cva('mb-8 grid gap-6 md:grid-cols-3');
const StepCardStyles = cva('flex gap-4');
const StepNumberStyles = cva(
  'text-green-dark mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accentBg text-sm font-bold',
);
const StepDescriptionStyles = cva(
  'text-orderImport-step-description text-orderImport-step-description-text font-normal',
);
const DownloadButtonContainerStyles = cva('mb-8');

const OrderImportInitialView = ({
  description,
  steps,
  downloadButtonLabel,
  uploadTitle,
  uploadSupportedText,
  processingError,
  isTemplateLinkLoading,
  isDownloadDisabled,
  downloadIcon,
  colorDisclaimer,
  onTemplateDownloadClick,
  onFileSelected,
  onDismissError,
}: OrderImportInitialViewProps) => {
  const { t } = useTranslation('account');

  return (
    <div data-testid="order-import-initial-view">
      <p className={SectionDescriptionStyles()} data-testid="order-import-description">
        {description}
      </p>

      <div className={StepsGridStyles()} data-testid="order-import-steps">
        {steps.map(step => (
          <article className={StepCardStyles()} data-testid={`order-import-step-${step.id}`} key={step.id}>
            <span className={StepNumberStyles()}>{step.number}</span>
            <div>
              <h6>{step.title}</h6>
              <p className={StepDescriptionStyles()}>{step.description}</p>
            </div>
          </article>
        ))}
      </div>

      <div className={DownloadButtonContainerStyles()} data-testid="order-import-template-download-container">
        <Button
          className={twMerge(isTemplateLinkLoading && '[&_svg]:animate-spin')}
          data-testid="order-import-template-download-button"
          disabled={isDownloadDisabled}
          icon={downloadIcon}
          iconPosition="left"
          label={downloadButtonLabel}
          onClick={onTemplateDownloadClick}
          type="secondary"
        />
      </div>

      {colorDisclaimer && (
        <p className={SectionDescriptionStyles()} data-testid="order-import-color-disclaimer">
          {colorDisclaimer}
        </p>
      )}

      <div className="relative">
        <OrderImportFileUpload
          data-testid="order-import-file-upload"
          onFileSelected={onFileSelected}
          supportedText={uploadSupportedText}
          title={uploadTitle}
        />

        {!!processingError && (
          <div
            className="rounded-orderImport-upload-section border-red absolute inset-0 z-10 flex flex-col items-center justify-center border-2 bg-red-50 p-10 text-center"
            data-testid="order-import-processing-error"
          >
            <FontAwesomeIcon className="text-red mb-4 h-10 w-10" icon={faTriangleExclamation} />
            <p className="text-red mb-6 max-w-[400px] text-base font-semibold">{processingError}</p>
            <Button label={String(t('orderImport.upload.dismissError'))} onClick={onDismissError} type="secondary" />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderImportInitialView;
